/**
 * `useActivityCapture()` — wires the standard auto-capture surfaces
 * for the activity log. Mount once near the top of the React tree
 * (inside `<UserActivityProvider>`).
 *
 * Surfaces:
 *   - **navigation**: `useLocation()` listener emits `nav.route_change`
 *     on every pathname change.
 *   - **clicks**: capture-phase delegated listener on `document`.
 *     Records interactive elements (`button`, `a`, `[role=button]`,
 *     `input[type=submit]`) only — non-interactive clicks are noise.
 *     Captures `tag`, `data-activity-id` (opt-in stable marker),
 *     truncated text content (≤80 chars), redacted by the provider.
 *   - **form submits**: delegated `submit` listener. Records form id
 *     + name + field names. Field VALUES are NOT captured unless
 *     the form opts in via `data-activity-capture-values="true"`.
 *   - **lifecycle**: `visibilitychange` (`page.hide`/`page.show`).
 *   - **JS errors**: handled by the provider's error-bridge wiring
 *     (Sentry beforeSend → activity log). This hook adds a window
 *     `error` + `unhandledrejection` listener as a backup for
 *     errors Sentry doesn't catch.
 *
 * API errors are NOT auto-captured here — they're surface-specific
 * (lexserver-client.ts non-2xx + gateway-client.ts chat.error frames)
 * and best wired at those call sites. Future PR can centralise via a
 * fetch wrapper.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useActivity } from "@/lib/activity/activity-context";

const CLICK_TEXT_MAX = 80;
const ERROR_MESSAGE_MAX = 1_024;

export function useActivityCapture(): void {
  const activity = useActivity();
  const location = useLocation();
  // Mirror activity into a ref so the navigation effect can omit it
  // from its dep list — otherwise the provider value changing once
  // (when useAuthIdentity resolves) would re-fire the effect for the
  // current pathname and emit a duplicate nav.route_change.
  const activityRef = useRef(activity);
  activityRef.current = activity;

  // ── navigation ──────────────────────────────────────────────────────────
  useEffect(() => {
    activityRef.current?.track("navigation", "nav.route_change", {
      to: location.pathname,
      search: location.search || undefined,
      hash: location.hash || undefined,
    });
  }, [location.pathname, location.search, location.hash]);

  // ── clicks ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activity) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const interactive = findInteractive(target);
      if (!interactive) return;
      activity.track("interaction", "ui.click", {
        tag: interactive.tagName.toLowerCase(),
        activity_id: interactive.getAttribute("data-activity-id") ?? undefined,
        role: interactive.getAttribute("role") ?? undefined,
        text: extractText(interactive),
        href: interactive instanceof HTMLAnchorElement ? interactive.href : undefined,
      });
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, [activity]);

  // ── form submits ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activity) return;
    const onSubmit = (event: SubmitEvent) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const captureValues =
        form.getAttribute("data-activity-capture-values") === "true";
      const fieldNames: string[] = [];
      const fieldValues: Record<string, string> = {};
      for (const el of form.elements) {
        const name = (el as { name?: string }).name;
        if (!name) continue;
        fieldNames.push(name);
        if (captureValues) {
          const v = (el as { value?: string }).value;
          if (typeof v === "string" && v.length > 0) {
            fieldValues[name] = v;
          }
        }
      }
      activity.track("form", "form.submit", {
        form_id: form.id || undefined,
        form_name: form.name || undefined,
        field_names: fieldNames,
        ...(captureValues ? { field_values: fieldValues } : {}),
      });
    };
    document.addEventListener("submit", onSubmit, { capture: true });
    return () => document.removeEventListener("submit", onSubmit, { capture: true });
  }, [activity]);

  // ── lifecycle ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activity) return;
    const onVisibility = () => {
      activity.track(
        "lifecycle",
        document.visibilityState === "hidden" ? "page.hide" : "page.show",
        { state: document.visibilityState },
      );
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [activity]);

  // ── JS errors (backup for things Sentry doesn't catch) ─────────────────
  useEffect(() => {
    if (!activity) return;
    const onError = (event: ErrorEvent) => {
      activity.track("error", "js.exception", {
        message: truncate(event.message ?? "<no message>", ERROR_MESSAGE_MAX),
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        source: "window.onerror",
      });
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as unknown;
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === "string"
            ? reason
            : "<non-error rejection>";
      activity.track("error", "js.exception", {
        message: truncate(message, ERROR_MESSAGE_MAX),
        source: "unhandledrejection",
      });
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [activity]);
}

// ── helpers ────────────────────────────────────────────────────────────────

function findInteractive(start: Element): Element | null {
  let cur: Element | null = start;
  for (let depth = 0; cur && depth < 6; depth += 1, cur = cur.parentElement) {
    if (
      cur.matches(
        "button, a, [role='button'], [role='link'], input[type='submit'], input[type='button'], summary",
      )
    ) {
      return cur;
    }
  }
  return null;
}

function extractText(el: Element): string | undefined {
  const aria = el.getAttribute("aria-label");
  if (aria) return aria.slice(0, CLICK_TEXT_MAX);
  const text = (el.textContent ?? "").trim().replace(/\s+/g, " ");
  if (!text) return undefined;
  return text.slice(0, CLICK_TEXT_MAX);
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 12)}…[truncated]`;
}
